import React, { useCallback, useEffect, useState } from 'react';
import { toast } from 'sonner';
import {
  Plus,
  Pencil,
  Trash,
  Eye,
  EyeSlash,
  Newspaper,
  Gear,
  ArrowSquareOut,
  Star,
} from '@phosphor-icons/react';
import { api } from '../../context/AuthContext';
import { Button } from '../../components/ui/button';
import { Card } from '../../components/ui/card';
import { PostEditorDialog } from '../../components/admin/posts/PostEditorDialog';
import { CategoryManager } from '../../components/admin/posts/CategoryManager';

const AdminPostsPage = () => {
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editorOpen, setEditorOpen] = useState(false);
  const [categoriesOpen, setCategoriesOpen] = useState(false);
  const [editing, setEditing] = useState(null);

  const load = useCallback(async () => {
    try {
      const [postsRes, catsRes] = await Promise.all([
        api.get('/admin/posts'),
        api.get('/admin/posts/categories'),
      ]);
      setPosts(postsRes.data);
      setCategories(catsRes.data);
    } catch (error) {
      toast.error('Failed to load posts');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
  }, [load]);

  const openCreate = () => {
    setEditing(null);
    setEditorOpen(true);
  };

  const openEdit = (post) => {
    setEditing(post);
    setEditorOpen(true);
  };

  const save = async (form) => {
    try {
      if (editing) {
        await api.put(`/admin/posts/${editing.id}`, form);
        toast.success('Post updated');
      } else {
        await api.post('/admin/posts', form);
        toast.success('Post created');
      }
      load();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to save post');
      throw error;
    }
  };

  const togglePublished = async (post) => {
    try {
      await api.put(`/admin/posts/${post.id}`, { published: !post.published });
      toast.success(post.published ? 'Post unpublished' : 'Post published');
      load();
    } catch (error) {
      toast.error('Failed to update post');
    }
  };

  const remove = async (post) => {
    if (!window.confirm(`Delete "${post.title}"? This cannot be undone.`)) return;
    try {
      await api.delete(`/admin/posts/${post.id}`);
      toast.success('Post deleted');
      load();
    } catch (error) {
      toast.error('Failed to delete post');
    }
  };

  return (
    <div className="min-h-screen bg-[rgb(var(--background))]" data-testid="admin-posts-page">
      <div className="container mx-auto px-6 py-8">
        <div className="flex items-center justify-between mb-8">
          <div>
            <h1 className="text-2xl font-heading font-semibold">News &amp; Stories</h1>
            <p className="text-sm text-foreground-muted">
              Everything published at /news — project updates, market reads and company news.
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant="outline"
              onClick={() => setCategoriesOpen(true)}
              className="border-black/10"
              data-testid="open-categories-button"
            >
              <Gear size={17} className="mr-2" /> Categories
            </Button>
            <Button onClick={openCreate} className="bg-primary hover:bg-primary-hover" data-testid="new-post-button">
              <Plus size={17} className="mr-2" /> New post
            </Button>
          </div>
        </div>

        {loading ? (
          <div className="flex justify-center py-20">
            <div className="animate-spin rounded-full h-10 w-10 border-t-2 border-b-2 border-primary" />
          </div>
        ) : posts.length === 0 ? (
          <Card className="p-12 text-center bg-white" data-testid="posts-empty-state">
            <Newspaper size={44} className="mx-auto mb-4 text-foreground-muted opacity-50" />
            <h3 className="text-lg font-semibold mb-2">No posts yet</h3>
            <p className="text-foreground-muted mb-6">Write the first story for the public page.</p>
            <Button onClick={openCreate} className="bg-primary hover:bg-primary-hover">
              <Plus size={17} className="mr-2" /> New post
            </Button>
          </Card>
        ) : (
          <Card className="overflow-hidden bg-white">
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead>
                  <tr className="border-b border-black/5 bg-[rgb(var(--background))]">
                    <th className="text-left py-4 px-6 text-sm font-medium text-foreground-muted">Title</th>
                    <th className="text-left py-4 px-6 text-sm font-medium text-foreground-muted">Category</th>
                    <th className="text-left py-4 px-6 text-sm font-medium text-foreground-muted">Status</th>
                    <th className="text-left py-4 px-6 text-sm font-medium text-foreground-muted">Date</th>
                    <th className="text-right py-4 px-6 text-sm font-medium text-foreground-muted">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {posts.map((post) => (
                    <tr
                      key={post.id}
                      className="border-b border-black/5 last:border-0 hover:bg-black/[0.02]"
                      data-testid={`post-row-${post.slug}`}
                    >
                      <td className="py-4 px-6">
                        <div className="flex items-start gap-2">
                          {post.is_featured && (
                            <Star size={14} weight="fill" className="text-primary mt-1 shrink-0" title="Featured" />
                          )}
                          <div>
                            <p className="font-medium">{post.title}</p>
                            <p className="text-sm text-foreground-muted">/news/{post.slug}</p>
                          </div>
                        </div>
                      </td>
                      <td className="py-4 px-6">
                        <span className="px-2.5 py-1 rounded-full text-xs font-medium bg-primary/10 text-primary">
                          {post.category_label || post.category}
                        </span>
                      </td>
                      <td className="py-4 px-6">
                        <span
                          className={`px-2.5 py-1 rounded-full text-xs font-medium ${
                            post.published ? 'bg-green-100 text-green-700' : 'bg-yellow-100 text-yellow-700'
                          }`}
                        >
                          {post.published ? 'Published' : 'Draft'}
                        </span>
                      </td>
                      <td className="py-4 px-6 text-sm text-foreground-muted">
                        {new Date(post.published_at || post.created_at).toLocaleDateString('en-GB', {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric',
                        })}
                      </td>
                      <td className="py-4 px-6">
                        <div className="flex items-center justify-end gap-1">
                          {post.published && (
                            <a
                              href={`/news/${post.slug}`}
                              target="_blank"
                              rel="noopener noreferrer"
                              className="p-2 hover:bg-black/5 rounded-lg transition-colors"
                              title="View live"
                              data-testid={`post-view-${post.slug}`}
                            >
                              <ArrowSquareOut size={17} className="text-foreground-muted" />
                            </a>
                          )}
                          <button
                            onClick={() => togglePublished(post)}
                            className="p-2 hover:bg-black/5 rounded-lg transition-colors"
                            title={post.published ? 'Unpublish' : 'Publish'}
                            data-testid={`post-publish-${post.slug}`}
                          >
                            {post.published ? (
                              <EyeSlash size={17} className="text-foreground-muted" />
                            ) : (
                              <Eye size={17} className="text-foreground-muted" />
                            )}
                          </button>
                          <button
                            onClick={() => openEdit(post)}
                            className="p-2 hover:bg-black/5 rounded-lg transition-colors"
                            title="Edit"
                            data-testid={`post-edit-${post.slug}`}
                          >
                            <Pencil size={17} className="text-foreground-muted" />
                          </button>
                          <button
                            onClick={() => remove(post)}
                            className="p-2 hover:bg-red-50 rounded-lg transition-colors"
                            title="Delete"
                            data-testid={`post-delete-${post.slug}`}
                          >
                            <Trash size={17} className="text-red-500" />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </Card>
        )}
      </div>

      {editorOpen && (
        <PostEditorDialog
          key={editing?.id || 'new'}
          open={editorOpen}
          onOpenChange={setEditorOpen}
          post={editing}
          categories={categories}
          onSave={save}
          onManageCategories={() => setCategoriesOpen(true)}
        />
      )}

      <CategoryManager
        open={categoriesOpen}
        onOpenChange={setCategoriesOpen}
        categories={categories}
        onChanged={load}
      />
    </div>
  );
};

export default AdminPostsPage;
